import { z } from 'zod';
// [12.2L-132] Per-brand conversion definition for strategist outcome
// scoring. Required before strategy_proposals can be scored — without
// it CPA-only scoring on undefined conversion semantics converges
// mag on cheap garbage conversions.
//
// Two source kinds:
//
//   - analytics_event:    a brand-side analytics event captured by
//                         the magig tracker (e.g. 'purchase',
//                         'qualified_lead'). identifier = event name.
//
//   - google_conversion:  a conversion action configured inside the
//                         brand's Google Ads account. identifier =
//                         the conversion action's resource id.
//
// qualityTier tells the scorer what KIND of conversion this is so it
// can apply the right weighting:
//
//   page_view      — landing-page hit. Easy to fire, low predictive
//                    value. Scorer flags hits as "informational only".
//   soft_lead      — newsletter / contact form. Cheap to acquire,
//                    weak predictor of revenue.
//   qualified_lead — sales-qualified lead, demo booked, trial start.
//                    Strong predictor; default tier for most B2B.
//   sale           — actual paid transaction. Highest weight.
//
// Brands without sales tracking should set qualityTier='qualified_lead'
// and the highest-quality event they DO track. We never pretend a
// page_view is a sale.
export const brandConversionDefinitionKindSchema = z.enum(['analytics_event', 'google_conversion']);
export const brandConversionDefinitionQualityTierSchema = z.enum([
    'page_view',
    'soft_lead',
    'qualified_lead',
    'sale',
]);
export const brandConversionDefinitionSchema = z.object({
    kind: brandConversionDefinitionKindSchema,
    identifier: z.string().min(1).max(200),
    qualityTier: brandConversionDefinitionQualityTierSchema,
});
// ─── Outcome window ──────────────────────────────────────────────────
//
// [spec 06106f46] How long a campaign must run before its outcome can
// be scored, keyed on conversion quality tier. Lifted here from
// apps/api/src/strategist/outcome-scoring.ts, which still owns the
// SCORING and imports this table.
//
// It lives in schemas because TWO lanes now need it and they must not
// drift: the scorer reads it to decide how long a window to measure,
// and campaign intake reads it to decide how long a window to
// RECOMMEND. A campaign closed before its tier window is a campaign
// the scorer answers 'inconclusive' about — an empty closing read, and
// noise fed into the hit-rate tiers that steer the next proposal. The
// two numbers are the same number, so they are the same constant.
//
// Longer tiers are not caution, they are cycle length: a sale closes
// slower than a newsletter signup, so the evidence arrives later.
/** Days a campaign must run before its outcome is scoreable, per
 *  conversion quality tier. Consumed by the strategist's outcome
 *  scorer (as the measurement window) and by campaign intake (as the
 *  floor on a recommended close date). */
export const DEFAULT_WINDOW_BY_TIER = {
    page_view: 14,
    soft_lead: 21,
    qualified_lead: 30,
    sale: 45,
};
/** The tier to assume when a brand has NO conversion definition.
 *  This file's own guidance tells brands without sales tracking to use
 *  `qualified_lead`, so an unset brand is treated the same way rather
 *  than handed the shortest window.
 *
 *  Note this is a window default ONLY. It does not make the brand
 *  scoreable: outcome scoring still refuses a brand with no
 *  conversion definition, so a caller relying on this default owes the
 *  founder that caveat. */
export const DEFAULT_WINDOW_TIER_WHEN_UNSET = 'qualified_lead';
/** Window floor in days for a brand's tier, falling back to
 *  `DEFAULT_WINDOW_TIER_WHEN_UNSET` when the brand has no conversion
 *  definition. Null and undefined are the same case here — neither is
 *  a claim that the window should be short. */
export function outcomeWindowDaysForTier(tier) {
    return DEFAULT_WINDOW_BY_TIER[tier ?? DEFAULT_WINDOW_TIER_WHEN_UNSET];
}
//# sourceMappingURL=brand-conversion-definition.js.map